import { useCallback, useRef, useState } from 'react'
import { ChevronDown, Menu, Search } from 'lucide-react'
import { cx } from '@/lib/clases'
import { useAtajo } from '@/lib/atajos'
import { formatearRNC } from '@/lib/rnc'
import { PuntoEstado } from '@/ui/InsigniaEstado'
import type { Tono } from '@/ui/tonos'
import { LogoCompacta } from '@/ui/marca/Logo'
import { IndicadorDGII, type EstadoDGII } from './IndicadorDGII'

/**
 * Barra superior de 56px con los tres elementos del documento:
 * selector de tenant (`Ctrl+K`), indicador de DGII y búsqueda global (`Ctrl+/`).
 *
 * El selector de tenant es, para un contador, el control más usado de toda la
 * aplicación, y muestra el semáforo de cada cliente en la misma lista.
 */

export interface TenantResumen {
  id: string
  nombre: string
  rnc: string
  /** Semáforo del cliente. En la consola del contador se explica en una línea. */
  semaforo: Tono
  /** Por qué está en ese color. Un semáforo que no se explica es decoración. */
  motivo?: string
}

export interface PropsBarraSuperior {
  tenants: readonly TenantResumen[]
  tenantActivo: TenantResumen
  onCambiarTenant: (id: string) => void
  estadoDGII: EstadoDGII
  pendientesDGII?: number
  horasDelMasViejo?: number
  usuario: { nombre: string; iniciales: string }
  /** En el teléfono la barra lateral no cabe: este botón la abre encima. Sin él no se muestra. */
  onAbrirMenu?: () => void
}

export function BarraSuperior({
  tenants,
  tenantActivo,
  onCambiarTenant,
  estadoDGII,
  pendientesDGII,
  horasDelMasViejo,
  usuario,
  onAbrirMenu,
}: PropsBarraSuperior) {
  const [selectorAbierto, setSelectorAbierto] = useState(false)
  const [consulta, setConsulta] = useState('')
  const campoBusqueda = useRef<HTMLInputElement>(null)

  useAtajo({ tecla: 'k', ctrl: true, dentroDeCampos: true }, () => setSelectorAbierto((v) => !v))
  useAtajo({ tecla: '/', ctrl: true, dentroDeCampos: true }, () => campoBusqueda.current?.focus())
  useAtajo(
    { tecla: 'Escape', dentroDeCampos: true },
    useCallback(() => setSelectorAbierto(false), []),
    selectorAbierto,
  )

  const filtrados = tenants.filter(
    (t) =>
      t.nombre.toLowerCase().includes(consulta.toLowerCase()) ||
      t.rnc.replace(/\D/g, '').includes(consulta.replace(/\D/g, '')),
  )

  return (
    <header
      className={cx(
        'sticky top-0 z-[var(--capa-barra)] flex h-[var(--alto-barra-superior)] items-center gap-3',
        'border-b border-gris-300 bg-white px-3 sin-impresion md:px-4',
      )}
    >
      {onAbrirMenu ? (
        <button
          type="button"
          aria-label="Abrir el menú"
          onClick={onAbrirMenu}
          className="-ml-1 flex size-[var(--alto-control)] shrink-0 items-center justify-center rounded-md text-gris-700 hover:bg-gris-100 md:hidden"
        >
          <Menu aria-hidden="true" className="size-5" />
        </button>
      ) : null}

      {/* El logo del archivo, no el nombre tecleado: el manual de marca no admite rehacerlo. */}
      <LogoCompacta className="hidden h-5 w-auto shrink-0 text-gris-900 sm:block" />

      {/* Selector de tenant ---------------------------------------------- */}
      <div className="relative min-w-0">
        <button
          type="button"
          aria-expanded={selectorAbierto}
          aria-haspopup="listbox"
          onClick={() => setSelectorAbierto((v) => !v)}
          className={cx(
            'flex h-[var(--alto-control)] items-center gap-2 rounded-md border border-gris-500',
            'bg-white px-2 text-cuerpo text-gris-900 hover:bg-gris-50',
          )}
        >
          <span className="max-w-[150px] truncate sm:max-w-[220px]">{tenantActivo.nombre}</span>
          <span className="mono hidden text-gris-600 sm:inline">{formatearRNC(tenantActivo.rnc)}</span>
          <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-gris-600" />
          {/* En el teléfono no hay teclado que anunciar. */}
          <span aria-hidden="true" className="mono hidden text-[11px] text-gris-600 md:inline">
            Ctrl+K
          </span>
        </button>

        {selectorAbierto ? (
          <div
            className={cx(
              'absolute left-0 top-full z-[var(--capa-panel)] mt-1 w-[min(360px,calc(100vw-1.5rem))] rounded-md',
              'border border-gris-300 bg-white p-1 shadow-flotante',
            )}
          >
            <input
              autoFocus
              value={consulta}
              onChange={(evento) => setConsulta(evento.target.value)}
              placeholder="Buscar cliente por nombre o RNC"
              aria-label="Buscar cliente"
              className="mb-1 h-[var(--alto-control)] w-full rounded-md border border-gris-500 px-2 text-cuerpo"
            />
            <ul role="listbox" aria-label="Clientes" className="max-h-72 overflow-auto">
              {filtrados.length === 0 ? (
                <li className="px-2 py-2 text-cuerpo text-gris-600">Sin resultados.</li>
              ) : null}
              {filtrados.map((tenant) => (
                <li key={tenant.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={tenant.id === tenantActivo.id}
                    onClick={() => {
                      onCambiarTenant(tenant.id)
                      setSelectorAbierto(false)
                    }}
                    className={cx(
                      'flex h-[var(--alto-fila-comoda)] w-full items-center gap-2 rounded-md px-2 text-left',
                      'hover:bg-gris-100',
                      tenant.id === tenantActivo.id && 'bg-marca-tenue',
                    )}
                  >
                    <PuntoEstado tono={tenant.semaforo} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-cuerpo text-gris-900">{tenant.nombre}</span>
                      {tenant.motivo ? (
                        <span className="block truncate text-etiqueta text-gris-600">{tenant.motivo}</span>
                      ) : null}
                    </span>
                    <span className="numero text-gris-600">{formatearRNC(tenant.rnc)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <div className="ml-auto flex items-center gap-2">
        <IndicadorDGII
          estado={estadoDGII}
          pendientes={pendientesDGII}
          horasDelMasViejo={horasDelMasViejo}
        />

        {/* Búsqueda global. En el teléfono no cabe junto al selector y se queda en las pantallas. */}
        <div className="relative hidden lg:block">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-gris-400"
          />
          <input
            ref={campoBusqueda}
            type="search"
            aria-label="Búsqueda global por e-NCF, RNC, cliente o monto"
            placeholder="Buscar e-NCF, RNC, cliente…"
            className={cx(
              'h-[var(--alto-control)] w-[280px] rounded-md border border-gris-500 bg-white',
              'pl-8 pr-12 text-cuerpo text-gris-900 hover:border-gris-600',
            )}
          />
          <span
            aria-hidden="true"
            className="mono pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-gris-600"
          >
            Ctrl+/
          </span>
        </div>

        <span
          title={usuario.nombre}
          className={cx(
            'flex size-[var(--alto-control)] items-center justify-center rounded-full',
            'border border-gris-300 bg-gris-50 text-etiqueta font-medium text-gris-700',
          )}
        >
          {usuario.iniciales}
        </span>
      </div>
    </header>
  )
}
